import * as PantryIcons from '@tocktix/pantry';
import { ClientBusinessDefinedState, dashboard } from 'types';
import { ComponentType } from 'react';

export type PantryIconComponent = ComponentType<{
  'aria-hidden'?: boolean;
  className?: string;
  color?: string;
  size?: 100 | 200 | 300;
}>;

// Icon used when a status has no stored icon and matches no mapping (spec: Fallback || Default).
export const PARTY_STATUS_FALLBACK_ICON = 'IconSquare';

// Default icon per primary state. Acts as the bucket-level fallback for custom
// statuses whose label is not in PARTY_STATUS_ICONS_BY_LABEL. (No PAID entry:
// PAID is not a primary-state bucket for business-defined states, so "Paid" is
// resolved by label instead.)
export const PARTY_STATUS_DEFAULT_ICONS: Partial<Record<dashboard.PartyState, string>> = {
  ARRIVED: 'IconMapMarkerFilled',
  CANCELED: 'IconNotAllowed',
  EXPECTED: 'IconBook',
  LEFT: 'IconChartPie100',
  NO_SHOW: 'IconNotAllowed',
  PARTIALLY_ARRIVED: 'IconMapMarkerFilled',
  PARTIALLY_SEATED: 'IconChartPie0',
  SEATED: 'IconChartPie0',
};

// Icon per built-in secondary state, from the Party Statuses design spec. Keyed
// by the normalized label (uppercased, spaces -> underscores; see
// PARTY_STATUS_ICONS_BY_LABEL usage). Only true secondary states belong here;
// statuses derived straight from a primary state are handled by
// PARTY_STATUS_DEFAULT_ICONS instead.
export const PARTY_STATUS_ICONS_BY_LABEL: Record<string, string> = {
  APPETIZER: 'IconChartPie25',
  ARRIVING_LATE: 'IconExclamation',
  DESSERT: 'IconChartPie75',
  ENTREE: 'IconChartPie50',
  PAID: 'IconCashCheckmark',
};

export type PartyStatusIconData = Pick<
  ClientBusinessDefinedState,
  'className' | 'strokeColorClassName' | 'border' | 'icon' | 'label' | 'primaryState' | 'key'
>;

export interface PartyStatusIconSelectOption {
  label: string;
  value: string;
}

export const PARTY_STATUS_ICON_OPTIONS: PartyStatusIconSelectOption[] = [
  { value: 'IconLock', label: 'Lock' },
  { value: 'IconUnlock', label: 'Unlock' },
  { value: 'IconNotAllowed', label: 'Not Allowed' },
  { value: 'IconCalendar', label: 'Calendar' },
  { value: 'IconCalendarCheckmark', label: 'Calendar Check' },
  { value: 'IconClipboard', label: 'Clipboard' },
  { value: 'IconEdit', label: 'Pencil' },
  { value: 'IconNote', label: 'Note' },
  { value: 'IconPostIt', label: 'Sticky' },
  { value: 'IconPostItCheckmark', label: 'Sticky Checkmark' },
  { value: 'IconListBullet', label: 'List' },
  { value: 'IconBook', label: 'Book' },
  { value: 'IconBookmark', label: 'Bookmark' },
  { value: 'IconBookmarkCheckmark', label: 'Bookmark Checkmark' },
  { value: 'IconPin', label: 'Pin' },
  { value: 'IconBubbleMessage', label: 'Message' },
  { value: 'IconThumbsUp', label: 'Thumbs Up' },
  { value: 'IconThumbsDown', label: 'Thumbs Down' },
  { value: 'IconPhone', label: 'Phone' },
  { value: 'IconClock', label: 'Clock' },
  { value: 'IconTimer', label: 'Timer' },
  { value: 'IconBell', label: 'Bell' },
  { value: 'IconMapMarkerFilled', label: 'Map Pin' },
  { value: 'IconChartPie0', label: 'Pie Chart 0%' },
  { value: 'IconChartPie25', label: 'Pie Chart 25%' },
  { value: 'IconChartPie30', label: 'Pie Chart 30%' },
  { value: 'IconChartPie50', label: 'Pie Chart 50%' },
  { value: 'IconChartPie60', label: 'Pie Chart 60%' },
  { value: 'IconChartPie75', label: 'Pie Chart 75%' },
  { value: 'IconChartPie100', label: 'Pie Chart 100%' },
  { value: 'IconPlaceCard', label: 'Place Card' },
  { value: 'IconChefHat', label: 'Chef Hat' },
  { value: 'IconCloche', label: 'Cloche' },
  { value: 'IconGlass', label: 'Glass' },
  { value: 'IconFlag', label: 'Flag' },
  { value: 'IconVerified', label: 'Verified' },
  { value: 'IconCreditCard', label: 'Credit Card' },
  { value: 'IconCash', label: 'Cash' },
  { value: 'IconCashCheckmark', label: 'Cash Check' },
  { value: 'IconInvoice', label: 'Invoice' },
  { value: 'IconPersonCheck', label: 'Person Check' },
  { value: 'IconPersonLock', label: 'Person Lock' },
  { value: 'IconPersonArrow', label: 'Person Arrow' },
  { value: 'IconWalkIn', label: 'Person Walking' },
  { value: 'IconArrowRight', label: 'Arrow Right' },
  { value: 'IconArrowLeft', label: 'Arrow Left' },
  { value: 'IconArrowDown', label: 'Arrow Down' },
  { value: 'IconArrowUp', label: 'Arrow Up' },
  { value: 'IconArrowRightCircle', label: 'Arrow Right Circle' },
  { value: 'IconArrowLeftCircle', label: 'Arrow Left Circle' },
  { value: 'IconArrowDownCircle', label: 'Arrow Down Circle' },
  { value: 'IconCrossLarge', label: 'Cross' },
  { value: 'IconCheckmark', label: 'Checkmark' },
  { value: 'IconExclamation', label: 'Exclamation' },
  { value: 'IconQuestion', label: 'Question Mark' },
  { value: 'IconPlusLarge', label: 'Plus' },
  { value: 'IconMinusLarge', label: 'Minus' },
  { value: 'IconSquare', label: 'Square' },
  { value: 'IconSquareFilled', label: 'Square Filled' },
  { value: 'IconSquareDashed', label: 'Square Dashed' },
  { value: 'IconCircle', label: 'Circle' },
  { value: 'IconCircleFilled', label: 'Circle Filled' },
  { value: 'IconCircleDashed', label: 'Circle Dashed' },
  { value: 'IconTriangle', label: 'Triangle' },
  { value: 'IconTriangleFilled', label: 'Triangle Filled' },
  { value: 'IconTriangleDashed', label: 'Triangle Dashed' },
];

export function getPartyStatusIcon(iconName?: string): PantryIconComponent | undefined {
  if (!iconName) {
    return undefined;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (PantryIcons as any)[iconName] as PantryIconComponent | undefined;
}

/**
 * Resolves the pantry icon name for a party status, in descending precedence:
 *
 *   1. `state.icon` — an icon explicitly stored on the status.
 *   2. `PARTY_STATUS_ICONS_BY_LABEL` — a per-label mapping for built-in secondary
 *      states, matched on the normalized label (trimmed, uppercased, with spaces
 *      replaced by underscores).
 *   3. `PARTY_STATUS_DEFAULT_ICONS` — the default icon for the status's primary
 *      state, used as the bucket-level fallback for custom labels.
 *   4. `PARTY_STATUS_FALLBACK_ICON` (`IconSquare`) — when nothing else matches.
 */
export function getMappedPartyStatusIconName(state: PartyStatusIconData): string {
  if (state?.icon) {
    return state.icon;
  }

  const labelKey = state?.label?.trim().toUpperCase().replace(/\s+/g, '_');
  if (labelKey && PARTY_STATUS_ICONS_BY_LABEL[labelKey]) {
    return PARTY_STATUS_ICONS_BY_LABEL[labelKey];
  }

  const primaryStateIcon = state?.primaryState
    ? PARTY_STATUS_DEFAULT_ICONS[state.primaryState]
    : undefined;
  return primaryStateIcon ?? PARTY_STATUS_FALLBACK_ICON;
}

export function getMappedPartyStatusIcon(
  state: PartyStatusIconData
): PantryIconComponent | undefined {
  return getPartyStatusIcon(getMappedPartyStatusIconName(state));
}
